"use client"

import type React from "react"
import { Link } from "react-router-dom"

interface Holiday {
  id: number
  name: string
  date: string
}

interface HolidayCardProps {
  holidays: Holiday[]
  permissions: string[]
}

const HolidayCard: React.FC<HolidayCardProps> = ({ holidays, permissions }) => {
  // Sort holidays by date
  const sortedHolidays = [...holidays].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingHolidays = sortedHolidays
    .filter((holiday) => new Date(holiday.date) >= today)
    .slice(0, 5);

  // Format date to be more readable
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  // Calculate days until holiday
  const daysUntil = (dateString: string) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const holidayDate = new Date(dateString)
    const diffTime = holidayDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return "Today"
    if (diffDays === 1) return "Tomorrow"
    return `${diffDays} days`
  }

  const totalUpcomingHolidays = sortedHolidays.filter((holiday) => new Date(holiday.date) >= today).length
  const hasMoreHolidays = totalUpcomingHolidays > 5

  return (
    <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Upcoming Holidays</h2>
          {permissions && permissions.includes("bulletin.add_holiday") && (
          <Link
            to="/bulletin/holidays/create/"
            className="w-8 h-8 flex items-center justify-center text-white bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 rounded-full transition-colors duration-200 shadow-sm mb-3"
            >
            <i className="fas fa-plus text-sm"></i>
          </Link>
          )}
        </div>
        {upcomingHolidays.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-10 text-gray-500 dark:text-gray-400">
            <i className="fas fa-calendar-times text-4xl mb-4 text-gray-400 dark:text-gray-500"></i>
            <p className="text-lg font-semibold">No upcoming holidays</p>
            <p className="text-sm">You're all clear for now — keep up the great work!</p>
          </div>
        ) : (
        <div className="space-y-3">
          {upcomingHolidays.map((holiday) => {
            const days = daysUntil(holiday.date)

            return (
              <div
                key={holiday.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  days === "Today"
                    ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30"
                    : "bg-blue-50 dark:bg-blue-900/20"
                }`}
              >
                <div className="flex items-center">
                  <div
                    className={`p-2 rounded-full ${
                      days === "Today"
                        ? "bg-green-200 dark:bg-green-800"
                        : "bg-blue-200 dark:bg-blue-800"
                    } mr-3`}
                  >
                    <i
                      className={`fas fa-calendar-day ${
                        days === "Today"
                          ? "text-green-600 dark:text-green-400"
                          : "text-blue-600 dark:text-blue-400"
                      }`}
                    ></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800 dark:text-white">{holiday.name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(holiday.date)}</p>
                  </div>
                </div>
                <div
                  className={`text-sm font-medium ${
                    days === "Today"
                      ? "text-green-600 dark:text-green-400"
                      : "text-blue-600 dark:text-blue-400"
                  }`}
                >
                  {days}
                </div>
              </div>
            )
          })}
        </div>
        )}

        {/* View All Holidays Button */}
        {(upcomingHolidays.length > 5 || hasMoreHolidays) && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
            <Link
              to="/holidays/"
              className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg transition-colors"
            >
              <i className="fas fa-calendar-alt mr-2"></i>
              View All Holidays
              {hasMoreHolidays && (
                <span className="ml-2 px-2 py-0.5 bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-xs rounded-full">
                  +{totalUpcomingHolidays - 5} more
                </span>
              )}
            </Link>
          </div>
        )}
      </div>
      
    </div>
  )
}

export default HolidayCard